'use client';
import React, { useState } from 'react';
import {
  Card,
  Button,
  StepperProgress,
  StepperCProgress,
  StepperChips,
  StepperArrow,
  Heading,
  STEPPER_STEPS,
} from '@ui';
import { withOptionalId } from '@lib/withOptionalId';
import { Section, Container } from '@layout';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';

interface StepperProps {
  sectionID?: string;
  steps?: string[];
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  isSpanish?: boolean;
}

const Stepper: React.FC<StepperProps> = ({
  isSpanish = false,
  sectionID = '',
  steps: stepsProp,
  classes = '',
  containerId,
  containerClasses,
}) => {
  const t = (en: string, es: string) => (isSpanish ? es : en);
  const [active, setActive] = useState(0);
  const derivedSteps: string[] =
    Array.isArray(STEPPER_STEPS) && STEPPER_STEPS.length ? STEPPER_STEPS : [];
  const stepsList = Array.isArray(stepsProp) && stepsProp.length ? stepsProp : derivedSteps;

  // Empty-state guard: be explicit when there are no steps to display
  if (!stepsList || stepsList.length === 0) {
    // Developers should provide steps via props or uiKit data
    // Return null to avoid rendering and to prevent division-by-zero
    console.warn('Stepper: no steps provided — nothing to render');
    return null;
  }

  const percent = Math.round(((active + 1) / stepsList.length) * 100);

  const handleNext = () => setActive((a) => Math.min(a + 1, stepsList.length - 1));
  const handlePrev = () => setActive((a) => Math.max(a - 1, 0));

  // active state and handlers are passed to StepperChips which handles keyboard

  return (
    <Section {...withOptionalId(sectionID)} className={classes}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <Heading as='h3'>{t('Stepper & Progress', 'Stepper y progreso')}</Heading>
        <p className='sub full'>
          {t(
            'A multi-step workflow component with next/previous navigation, plus visual linear and circular progress indicators that update as you advance through each step.',
            'Componente de flujo multi-paso con navegacion siguiente/anterior, mas indicadores visuales de progreso lineal y circular que se actualizan al avanzar en cada paso.'
          )}
        </p>
        <Card className='stepper'>
          <div className='card-header'>
            <Heading as='h4'>{t('Steppers', 'Steppers')}</Heading>
          </div>
          <div className='steppers'>
            <StepperChips
              steps={stepsList}
              active={active}
              onChange={setActive}
              id='stepper1'
              className='mb-16'
            />
            <StepperArrow
              steps={stepsList}
              active={active}
              onChange={setActive}
              id='stepper2'
              className='stepper-sm'
            />
            <div className='stepper stepper-progress row'>
              <div style={{ minWidth: 240, flex: 1 }}>
                <StepperProgress percent={percent} />
                <small className='hint'>
                  {t('Linear', 'Lineal')}: {percent}%
                </small>
              </div>
              <div className='flex-column flex-center'>
                <StepperCProgress percent={percent} />
                <small className='hint'>{t('Circular', 'Circular')}</small>
              </div>
            </div>
            <div className='stepper-actions row btn-row'>
              <Button
                label={t('Back', 'Atras')}
                className='btn-ghost'
                onClick={handlePrev}
                disabled={active === 0}
                data-prev-step
                aria-label={t('Previous step', 'Paso anterior')}
              />
              <Button
                label={t('Next', 'Siguiente')}
                className='btn-primary'
                onClick={handleNext}
                disabled={active === stepsList.length - 1}
                data-next-step
                aria-label={t('Next step', 'Siguiente paso')}
              />
            </div>
          </div>
        </Card>
        <MarkupSnippet
          {...markupCopyLabels(isSpanish)}
          code={`<ol class="stepper-list" role="list">
  <li class="stepper-step is-active">
    <span class="stepper-step-label">1. Discovery</span>
  </li>
  <li class="stepper-step">
    <span class="stepper-step-label">2. Design</span>
  </li>
</ol>
<div class="progress-bar" role="progressbar" aria-valuenow="25" aria-valuemin="0" aria-valuemax="100"></div>`}
        />
      </Container>
    </Section>
  );
};

export default Stepper;
